import React, { useEffect, useState } from 'react';
import { FabricObject } from 'fabric';
import { PauseCircleIcon, PlayCircleIcon, StopCircleIcon, Trash2 } from 'lucide-react';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useMedia } from '../hooks/use-media';
import { ToolBtn } from '../../shared/ui/tool-btn';

export const VIDEO_SETTINGS_KEY = 'video-settings';

export const VideoSettings: React.FC = () => {
  const { canvas, selectedObject, deleteSelected } = useEditorCanvas();
  const { getVideo } = useMedia();

  const id = (selectedObject as (FabricObject & { id?: string }) | null)?.id;
  const videoEl = id ? getVideo(id) : null;

  const [isPlaying, setIsPlaying] = useState(!!videoEl && !videoEl.paused);

  useEffect(() => {
    if (!videoEl) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    videoEl.addEventListener('play', onPlay);
    videoEl.addEventListener('pause', onPause);
    setIsPlaying(!videoEl.paused);
    return () => {
      videoEl.removeEventListener('play', onPlay);
      videoEl.removeEventListener('pause', onPause);
    };
  }, [videoEl]);

  // Fabric v7 doesn't render video sources on its own. Drive a repaint on
  // every decoded frame: requestVideoFrameCallback ticks at the video's
  // framerate and naturally stops while the video is paused/ended.
  useEffect(() => {
    if (!videoEl || !canvas) return;
    let rvfcId: number | null = null;
    const onFrame: VideoFrameRequestCallback = () => {
      canvas.requestRenderAll();
      rvfcId = videoEl.requestVideoFrameCallback(onFrame);
    };
    rvfcId = videoEl.requestVideoFrameCallback(onFrame);
    return () => {
      if (rvfcId !== null) videoEl.cancelVideoFrameCallback(rvfcId);
    };
  }, [videoEl, canvas]);

  if (!canvas || !selectedObject || !videoEl) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <ToolBtn
        onClick={() => (videoEl.paused ? videoEl.play() : videoEl.pause())}
        title={isPlaying ? 'Pausar' : 'Reproducir'}
      >
        {isPlaying ? <PauseCircleIcon size={18} /> : <PlayCircleIcon size={18} />}
      </ToolBtn>
      <ToolBtn
        onClick={() => {
          videoEl.pause();
          videoEl.currentTime = 0;
        }}
        title='Detener'
      >
        <StopCircleIcon size={18} />
      </ToolBtn>
      <ToolBtn onClick={deleteSelected} variant='danger' title='Eliminar'>
        <Trash2 size={15} />
      </ToolBtn>
    </div>
  );
};
